import React, { useCallback, useEffect, useRef, useState } from "react";
import api from "@/utils/api";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Check, Lock, Palette, RefreshCw, Sparkles, X } from "lucide-react";
import { useNavigate } from "react-router-dom";

export const LEX_CLOSING_TEXT_DEFAULT = "Ich danke für das entgegengebrachte Vertrauen.";
const HEX_RE = /^#[0-9a-fA-F]{6}$/;

function DesignTile({ d, selected, teasing, onSelect, onTease, accent }) {
  const locked = !d.allowed;
  const tint = d.key === "klar" ? d.default_accent : accent;
  return (
    <button
      type="button"
      data-testid={`design-tile-${d.key}`}
      aria-pressed={selected}
      onClick={() => (locked ? onTease(d.key) : onSelect(d.key))}
      className={`relative text-left rounded-xl border-2 p-2 transition-colors ${selected ? "border-emerald-500 bg-emerald-50/40" : teasing ? "border-amber-400 bg-amber-50/40" : "border-slate-200 hover:border-slate-300"} ${locked ? "opacity-80" : ""}`}
    >
      <div className="relative overflow-hidden rounded-md border border-slate-100 bg-white">
        <img src={`/design-thumbs/${d.key}.png`} alt={`Design ${d.name}`} className="w-full" />
        {d.key !== "klar" && <span aria-hidden className="absolute inset-x-0 top-0 h-1.5" style={{ backgroundColor: tint }} data-testid={`design-tile-tint-${d.key}`} />}
      </div>
      <div className="mt-2 flex items-center justify-between">
        <span className="flex items-center gap-1.5 text-sm font-semibold text-slate-800"><span className="inline-block h-2.5 w-2.5 rounded-full" style={{ backgroundColor: tint }} />{d.name}</span>
        {selected && <Check className="h-4 w-4 text-emerald-600" data-testid={`design-tile-selected-${d.key}`} />}
        {locked && <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700"><Lock className="h-3 w-3" /> Basic</span>}
      </div>
      <p className="mt-1 text-xs text-slate-500 leading-snug">{d.description}</p>
    </button>
  );
}

export default function InvoiceDesignSection({ settings, isLex, user, onSaved }) {
  const cfg = settings?.invoice_design || {};
  const [meta, setMeta] = useState(null);
  const [design, setDesign] = useState(cfg.design || "klar");
  const [accent, setAccent] = useState(cfg.accent_color || "#1e40af");
  const [closingText, setClosingText] = useState(cfg.closing_text ?? (isLex ? LEX_CLOSING_TEXT_DEFAULT : ""));
  const [hideMadeWith, setHideMadeWith] = useState(!!cfg.hide_made_with);
  const [saving, setSaving] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [teaserDesign, setTeaserDesign] = useState(null);
  const timer = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const c = settings?.invoice_design || {};
    setDesign(c.design || "klar");
    setAccent(c.accent_color || "#1e40af");
    setClosingText(c.closing_text ?? (isLex ? LEX_CLOSING_TEXT_DEFAULT : ""));
    setHideMadeWith(!!c.hide_made_with);
  }, [settings, isLex]);

  useEffect(() => {
    api.get("/invoices/designs").then((r) => setMeta(r.data)).catch(() => setMeta(null));
  }, []);

  const loadPreview = useCallback(async (d, a, hide, teaser = false) => {
    setPreviewLoading(true);
    try {
      const res = await api.post("/invoices/design-preview", { design: d, accent_color: HEX_RE.test(a) ? a : undefined, hide_made_with: hide, format: "png", teaser }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      setPreviewUrl((old) => { if (old) URL.revokeObjectURL(old); return url; });
    } catch (e) {
      toast.error("Vorschau konnte nicht geladen werden");
    } finally {
      setPreviewLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!meta) return undefined;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => loadPreview(teaserDesign || design, accent, hideMadeWith, !!teaserDesign), 500);
    return () => clearTimeout(timer.current);
  }, [design, accent, hideMadeWith, meta, loadPreview, teaserDesign]);

  const teaserName = teaserDesign ? meta?.designs?.find((d) => d.key === teaserDesign)?.name : null;

  const canChoose = !!meta?.can_choose;
  const canHide = !!meta?.can_hide_badge;

  const save = async () => {
    if (!HEX_RE.test(accent)) { toast.error("Bitte eine gültige Farbe wie #1e40af eingeben"); return; }
    setSaving(true);
    try {
      const payload = { ...cfg, closing_text: closingText.trim() };
      if (!isLex) {
        payload.design = canChoose ? design : "klar";
        payload.accent_color = accent.toLowerCase();
        payload.hide_made_with = canHide ? hideMadeWith : false;
      }
      const res = await api.put("/settings", { invoice_design: payload });
      toast.success(isLex ? "Honorarnoten-Design gespeichert" : "Rechnungsdesign gespeichert");
      onSaved?.(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const cardCls = isLex ? "rounded-none border-slate-200" : "";
  return (
    <div className="space-y-6" data-testid="invoice-design-section">
      <Card className={cardCls}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base"><Palette className="h-4 w-4" />{isLex ? "Honorarnoten-Design" : "Rechnungsdesign"}</CardTitle>
          <CardDescription>
            {isLex
              ? "Ihre Honorarnoten erscheinen im lex-Design (Navy/Gold, Serifenschrift). Das Kanzlei-Logo aus den Stammdaten wird oben rechts platziert (max. 60 × 22 mm)."
              : "Wähle ein Design und deine Akzentfarbe – die Vorschau rechts zeigt eine Musterrechnung mit deinen Firmendaten. Angebote und Mahnungen übernehmen das Design."}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-8 lg:grid-cols-[1fr_360px]">
            <div className="space-y-6">
              {!isLex && meta && (
                <>
                  {!canChoose && (
                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" data-testid="design-upgrade-hint">
                      <Lock className="mr-1 inline h-3.5 w-3.5" /> Weitere Designs und die Akzentfarbe sind ab dem Basic-Plan wählbar. Dein Design: <b>Klar</b>.
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-3" data-testid="design-tiles">
                    {meta.designs.map((d) => <DesignTile key={d.key} d={d} accent={HEX_RE.test(accent) ? accent : d.default_accent} selected={design === d.key} teasing={teaserDesign === d.key} onSelect={(k) => { setTeaserDesign(null); setDesign(k); }} onTease={setTeaserDesign} />)}
                  </div>
                  <div className="space-y-2">
                    <Label>Akzentfarbe</Label>
                    <div className="flex flex-wrap items-center gap-2">
                      {meta.accent_presets.map((c) => (
                        <button key={c} type="button" data-testid={`accent-preset-${c.slice(1)}`} disabled={!canChoose} onClick={() => setAccent(c)}
                          aria-label={`Farbe ${c}`}
                          className={`h-8 w-8 rounded-full border-2 transition-transform ${accent.toLowerCase() === c ? "border-slate-900 scale-110" : "border-white shadow"} ${!canChoose ? "opacity-50" : ""}`}
                          style={{ backgroundColor: c }} />
                      ))}
                      <Input data-testid="accent-hex-input" value={accent} disabled={!canChoose} onChange={(e) => setAccent(e.target.value)} className="h-8 w-28 font-mono text-xs" placeholder="#1e40af" />
                    </div>
                    <p className="text-xs text-slate-500">Bei „Klar“ bleibt das bewährte Blau; die Farbe wirkt in den anderen Designs (Kopf, Tabelle, Gesamtbetrag).</p>
                  </div>
                </>
              )}

              <div className="space-y-2">
                <Label htmlFor="closing-text">Standard-Schlusstext</Label>
                <Textarea id="closing-text" data-testid="invoice-closing-text" value={closingText} maxLength={300} rows={2}
                  placeholder={isLex ? LEX_CLOSING_TEXT_DEFAULT : "z. B. Vielen Dank für Ihren Auftrag – wir freuen uns auf die weitere Zusammenarbeit."}
                  onChange={(e) => setClosingText(e.target.value)} />
                <p className="text-xs text-slate-500">
                  Wird bei jeder neuen {isLex ? "Honorarnote" : "Rechnung"} als Notiz vorbelegt und kann dort angepasst oder gelöscht werden. {closingText.length}/300
                </p>
              </div>

              {!isLex && (
                <div className="flex items-start justify-between gap-4 rounded-lg border border-slate-200 p-4">
                  <div className="space-y-1">
                    <Label htmlFor="hide-made-with" className="flex items-center gap-2">
                      Schriftzug „erstellt mit ivoliq.io“ ausblenden
                      {!canHide && <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700"><Lock className="h-3 w-3" /> Pro</span>}
                    </Label>
                    <p className="text-xs text-slate-500">Der dezente Hinweis steht in der Fußzeile. In Free und Basic ist er immer sichtbar, ab Pro kannst du ihn ausblenden.</p>
                  </div>
                  <Switch id="hide-made-with" data-testid="invoice-hide-made-with" checked={canHide ? hideMadeWith : false} disabled={!canHide} onCheckedChange={setHideMadeWith} />
                </div>
              )}

              <div className="flex justify-end">
                <Button onClick={save} disabled={saving} data-testid="invoice-design-save" className={isLex ? "rounded-none bg-[#0A1128] hover:bg-[#0A1128]/90" : ""}>
                  {saving ? "Speichern …" : "Speichern"}
                </Button>
              </div>
            </div>

            <div className="space-y-2 lg:sticky lg:top-24 lg:self-start" data-testid="design-preview-panel">
              <div className="flex items-center justify-between">
                <Label>Vorschau (Musterrechnung)</Label>
                <Button type="button" variant="ghost" size="sm" data-testid="design-preview-refresh" onClick={() => loadPreview(design, accent, hideMadeWith)} disabled={previewLoading}>
                  <RefreshCw className={`h-3.5 w-3.5 ${previewLoading ? "animate-spin" : ""}`} />
                </Button>
              </div>
              {teaserDesign && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="design-teaser-banner">
                  <div className="flex items-start justify-between gap-2">
                    <div className="text-sm text-amber-900">
                      <Sparkles className="mr-1 inline h-4 w-4" /> So sähe deine Rechnung im Design <b>„{teaserName}“</b> aus – verfügbar ab <b>Basic</b>.
                    </div>
                    <button type="button" aria-label="Teaser schließen" onClick={() => setTeaserDesign(null)} data-testid="design-teaser-close" className="text-amber-700 hover:text-amber-900"><X className="h-4 w-4" /></button>
                  </div>
                  <Button size="sm" className="mt-2 bg-amber-600 hover:bg-amber-700" data-testid="design-teaser-upgrade" onClick={() => { navigate("/settings?tab=plan"); window.scrollTo({ top: 0, behavior: "smooth" }); }}>Jetzt upgraden</Button>
                </div>
              )}
              <div className={`aspect-[210/297] w-full overflow-hidden rounded-lg border bg-slate-50 ${teaserDesign ? "border-amber-300" : "border-slate-200"}`}>
                {previewUrl ? (
                  <img alt="Design-Vorschau" data-testid="design-preview-frame" src={previewUrl} className="h-full w-full object-contain" />
                ) : (
                  <div className="flex h-full items-center justify-center text-sm text-slate-400">{previewLoading ? "Vorschau wird gerendert …" : "Keine Vorschau"}</div>
                )}
              </div>
              {previewUrl && <a href={previewUrl} target="_blank" rel="noreferrer" className="text-xs text-emerald-700 underline" data-testid="design-preview-open">Vorschau groß öffnen</a>}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
